/* eslint-disable react/prop-types */
import React, { createRef, useEffect, useRef } from "react";
import QuizConfirmAnswers from "../../QuizConfirmAnswers/jsx/index.jsx";
import { useQuiz } from "src/contexts/QuizContext.js";
import { quizInteractionTracking } from "src/modules/QuizHelpers/index.js";

function QuizQuestionOptions({ questionKey, sectionKey, question, dispatch }) {
    const { windowWidth } = useQuiz();
    const allOptions = question.subquestions[sectionKey].options;
    const section = question.subquestions[sectionKey];

    let refArr = [];

    const handleClick = (e, option, optionIndex) => {
        e.preventDefault();
        // Comprehensive focus reset for mobile devices
        e.target.blur();
        if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
        }
        document.body.focus();
        document.body.blur();

        // Get question number from questionKey (e.g., "question1" -> "Q1")
        const questionNumber = questionKey.replace("question", "Q");

        // Get sub question index from sectionKey (e.g., "section1" -> 1)
        const subQuestionIndex = sectionKey.replace("section", "");
        const subQuestionIndexNum = parseInt(subQuestionIndex, 10) || null;

        // Get selected answer index (1-based)
        const selectedAnswerIndex = optionIndex + 1;

        // Check if answer changed (had a previous answer)
        // Use savedAnswer first (persisted), then currentAnswer (temporary)
        // Only true if there was a previous answer AND it's different from current selection
        const previousAnswer = section.savedAnswer || section.currentAnswer;
        const answerChanged = previousAnswer ? previousAnswer !== option : false;

        // Determine if this is a sub-question (multiple sections) or main question
        const isSubQuestion = Object.keys(question.subquestions).length > 1;

        // Answer a question or Answer a sub question
        quizInteractionTracking({
            interaction_type: "select",
            question_number: questionNumber,
            sub_question_index: isSubQuestion ? subQuestionIndexNum : null,
            selected_answer_index: selectedAnswerIndex,
            answer_changed: answerChanged,
            navigation_action: null,
        });

        const delay = windowWidth < 1024 ? 500 : 0;
        const type = windowWidth < 1024 ? "saveAnswer" : "currentAnswer";
        setTimeout(() => {
            dispatch({
                type,
                payload: {
                    questionKey: questionKey,
                    sectionKey: sectionKey,
                    value: option,
                },
            });
            if (delay)
                dispatch({
                    type: "qContainerScroll",
                });
        }, delay);
    };

    // set focus
    useEffect(() => {
        if (allOptions.length === refArr.length) {
            allOptions.forEach((option, index) => {
                if (option === section.currentAnswer && refArr[index].current) {
                    refArr[index].current.focus();
                }
            });
        }
    }, [refArr, allOptions]);

    useEffect(() => {
        const handler = () => {
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
        };
        document.addEventListener("mouseup", handler);
        return () => document.removeEventListener("mouseup", handler);
    }, []);

    const renderOptions = allOptions.map((option, index) => {
        const newRef = createRef();
        refArr.push(newRef);

        return (
            <li
                key={option}
                className={`quiz-question__options-button ${option === section.currentAnswer ? "selected" : ""}`}
            >
                <input
                    id={`option-${index}`}
                    type="radio"
                    name={`radio-group-${section.id}`}
                    value={option}
                    defaultChecked={option === section.currentAnswer}
                ></input>
                <label
                    ref={newRef}
                    tabIndex={1 + index}
                    htmlFor={`option-${index}`}
                    onClick={(e) => handleClick(e, option, index)}
                    onKeyDown={(e) => {
                        if (e.key === " ") handleClick(e, option, index);
                    }}
                >
                    {option}
                </label>
            </li>
        );
    });

    // do we need conformation for a single section question?
    const displayRender = () => {
        return (
            question.status == "start" ||
            question.status == "progress" ||
            Object.keys(question.subquestions).length == 1
        );
    };

    return displayRender() ? (
        <>
            {section.title ? (
                <span className="quiz-question__range-label">
                    {section.title}
                </span>
            ) : (
                ``
            )}
            <div className="quiz-question__options">
                <ul className="quiz-question__options-list">{renderOptions}</ul>
            </div>
        </>
    ) : (
        windowWidth > 1024 && <QuizConfirmAnswers _question={question} />
    );
}

export default QuizQuestionOptions;
